import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  X,
  Save,
  Lock,
  FileText,
  User,
  Briefcase,
  Receipt,
  DollarSign,
  Clock,
  Calculator,
  CreditCard,
  FileStack,
  BarChart3,
  MessageSquare,
  Building2,
  ImageIcon,
  PenTool,
  Scissors,
} from "lucide-react";
import { useBuilder, BlockConfig, generateBlockId } from "./BuilderContext";
import { billingTemplatesApi } from "../../../services/api";
import Toggle from "../../ui/Toggle";

// ── Block metadata ────────────────────────────────────────────────────────

const BLOCK_META: Record<string, { label: string; icon: React.FC<any> }> = {
  header: { label: "Header", icon: FileText },
  recipient: { label: "Recipient", icon: User },
  matter_details: { label: "Matter Details", icon: Briefcase },
  fees_table: { label: "Fees", icon: Receipt },
  disbursements_table: { label: "Disbursements", icon: DollarSign },
  time_entries_table: { label: "Time Entries", icon: Clock },
  totals_summary: { label: "Totals", icon: Calculator },
  payment_history: { label: "Payments", icon: CreditCard },
  invoice_history: { label: "Invoice History", icon: FileStack },
  per_matter_breakdown: { label: "Per-Matter Breakdown", icon: BarChart3 },
  notes: { label: "Notes", icon: MessageSquare },
  footer: { label: "Footer", icon: Building2 },
  image: { label: "Image", icon: ImageIcon },
  signature: { label: "Signature", icon: PenTool },
  page_break: { label: "Page Break", icon: Scissors },
};

// ── Default block settings factory ────────────────────────────────────────

function createDefaultSettings(): BlockConfig["settings"] {
  return {
    variant: "compact",
    headingOverride: null,
    columnsVisible: [],
  };
}

function createNewBlock(
  blockType: string,
  order: number,
  overrides?: Partial<BlockConfig["settings"]>
): BlockConfig {
  return {
    id: generateBlockId(),
    blockType,
    enabled: true,
    locked: false,
    order,
    settings: { ...createDefaultSettings(), ...overrides },
  };
}

// ── Component ─────────────────────────────────────────────────────────────

const BlockDrawer: React.FC = () => {
  const { state, dispatch } = useBuilder();
  const [isExpanded, setIsExpanded] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const sortedBlocks = [...state.blocks].sort((a, b) => a.order - b.order);

  // ── Save as template ──────────────────────────────────────────────────

  const handleSaveAsTemplate = async () => {
    const name = window.prompt("Template name:");
    if (!name) return;
    setSavingTemplate(true);
    try {
      await billingTemplatesApi.create({
        name,
        documentType:
          state.documentType === "invoice" ? "invoice" : "account_summary",
        blocks: state.blocks,
      });
      alert("Template saved!");
    } catch (err) {
      console.error("Failed to save template:", err);
    } finally {
      setSavingTemplate(false);
    }
  };

  // ── Add block helpers ─────────────────────────────────────────────────

  const handleAddBlock = (
    blockType: string,
    settingsOverrides?: Partial<BlockConfig["settings"]>
  ) => {
    const newBlock = createNewBlock(
      blockType,
      state.blocks.length,
      settingsOverrides
    );
    dispatch({ type: "ADD_BLOCK", block: newBlock });
  };

  // ── Collapsed rail ────────────────────────────────────────────────────

  const renderCollapsedRail = () => (
    <div className="w-14 bg-white border-r border-slate-200 flex flex-col items-center py-4 print:hidden">
      {/* Open drawer button */}
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors mb-4"
        title="Add / manage blocks"
      >
        <Plus className="h-4.5 w-4.5" />
      </button>

      {/* Block type icons */}
      <div className="flex-1 flex flex-col items-center gap-2 overflow-y-auto">
        {sortedBlocks.map((block) => {
          const meta = BLOCK_META[block.blockType] || {
            label: block.blockType,
            icon: FileText,
          };
          const Icon = meta.icon;

          return (
            <div key={block.id} className="relative" title={meta.label}>
              <Icon
                className={`h-4 w-4 ${
                  block.enabled ? "text-slate-500" : "text-slate-300"
                }`}
              />
              {/* Status dot */}
              <span
                className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${
                  block.enabled ? "bg-emerald-500" : "bg-slate-300"
                }`}
              />
            </div>
          );
        })}
      </div>

      {/* Save button at bottom */}
      <button
        type="button"
        onClick={handleSaveAsTemplate}
        disabled={savingTemplate}
        className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors mt-4 disabled:opacity-50"
        title="Save as template"
      >
        <Save className="h-4 w-4" />
      </button>
    </div>
  );

  // ── Expanded panel ────────────────────────────────────────────────────

  const renderExpandedPanel = () => (
    <AnimatePresence>
      {isExpanded && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            className="fixed inset-0 bg-black/10 z-40 print:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setIsExpanded(false)}
          />

          {/* Panel */}
          <motion.div
            className="absolute top-0 left-0 bottom-0 w-[280px] bg-white shadow-xl z-50 flex flex-col print:hidden"
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  Document Blocks
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Toggle to show/hide sections
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Block list */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
              <div className="space-y-1">
                {sortedBlocks.map((block) => {
                  const meta = BLOCK_META[block.blockType] || {
                    label: block.blockType,
                    icon: FileText,
                  };
                  const Icon = meta.icon;

                  return (
                    <div
                      key={block.id}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl transition-colors ${
                        block.enabled
                          ? "bg-white hover:bg-slate-50"
                          : "bg-slate-50/50"
                      }`}
                    >
                      {/* Toggle switch */}
                      <Toggle
                        checked={block.enabled}
                        onChange={() =>
                          dispatch({
                            type: "TOGGLE_BLOCK",
                            blockId: block.id,
                          })
                        }
                        disabled={block.locked}
                        size="sm"
                      />

                      {/* Icon + label */}
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 ${
                          block.enabled ? "text-slate-600" : "text-slate-300"
                        }`}
                      />
                      <span
                        className={`text-sm font-medium flex-1 truncate ${
                          block.enabled ? "text-slate-700" : "text-slate-400"
                        }`}
                      >
                        {meta.label}
                      </span>

                      {/* Lock icon */}
                      {block.locked && (
                        <Lock className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 my-4" />

              {/* Add Block section */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5 px-1">
                  Add Block
                </h4>
                <div className="space-y-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      handleAddBlock("image", {
                        imageKey: null,
                        imageWidth: 100,
                        imageAlign: "center",
                      })
                    }
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-dashed border-slate-200 text-sm font-medium text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Image
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleAddBlock("signature", {
                        signatureLabel: "Signature",
                        signatureShowDate: true,
                        signatureShowName: true,
                      })
                    }
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-dashed border-slate-200 text-sm font-medium text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
                  >
                    <PenTool className="h-4 w-4" />
                    Signature
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddBlock("page_break")}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-dashed border-slate-200 text-sm font-medium text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
                  >
                    <Scissors className="h-4 w-4" />
                    Page Break
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 my-4" />

              {/* Save as Template */}
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all disabled:opacity-50 disabled:hover:border-slate-300 disabled:hover:text-slate-500 disabled:hover:bg-transparent"
              >
                <Save className="h-4 w-4" />
                {savingTemplate ? "Saving..." : "Save as Template"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // ── Main render ───────────────────────────────────────────────────────

  return (
    <div className="relative h-full print:hidden">
      {renderCollapsedRail()}
      {renderExpandedPanel()}
    </div>
  );
};

export default BlockDrawer;
